/**
 * CategoryTreeSelect - Seletor hierárquico de categorias
 * 
 * Características:
 * - Exibição em árvore com indentação
 * - Busca por nome
 * - Suporte a exclusão de categoria (evita loops)
 * - Opção para selecionar "Nenhuma categoria"
 * 
 * @author Sistema
 * @version 1.0.0
 */

import * as React from 'react';
import { Check, ChevronsUpDown, FolderTree, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useCrmCategories, CrmProductCategory } from '@/hooks/useCrmCategories';

// Props do componente
interface CategoryTreeSelectProps {
  /** ID da categoria selecionada */
  value: string | null;
  /** Callback ao selecionar categoria */
  onChange: (categoryId: string | null) => void;
  /** Placeholder do botão */
  placeholder?: string;
  /** ID da categoria a excluir (evita seleção circular) */
  excludeId?: string;
  /** Mostrar opção "Sem categoria" */
  showRoot?: boolean;
  /** Desabilitar seletor */
  disabled?: boolean;
  /** Classes CSS adicionais */
  className?: string;
}

/**
 * Renderiza opções de categoria com hierarquia
 */
function flattenCategories(
  categories: CrmProductCategory[],
  excludeId?: string,
  level = 0
): Array<{ id: string; name: string; level: number; icon: string; color: string }> {
  const result: Array<{ id: string; name: string; level: number; icon: string; color: string }> = [];

  for (const category of categories) {
    // Pula categoria excluída e seus filhos
    if (category.id === excludeId) continue;

    result.push({
      id: category.id,
      name: category.name,
      level,
      icon: category.icon,
      color: category.color,
    });

    // Adiciona filhos recursivamente
    if (category.children?.length) {
      result.push(...flattenCategories(category.children, excludeId, level + 1));
    }
  }

  return result;
}

/**
 * Componente seletor de categoria em árvore
 */
export function CategoryTreeSelect({
  value,
  onChange,
  placeholder = 'Selecione uma categoria',
  excludeId,
  showRoot = true,
  disabled = false,
  className,
}: CategoryTreeSelectProps) {
  const [open, setOpen] = React.useState(false);
  const { categoryTree, categories, isLoading } = useCrmCategories();

  // Achata a árvore para exibição
  const flatCategories = React.useMemo(
    () => flattenCategories(categoryTree, excludeId),
    [categoryTree, excludeId]
  );

  // Encontra categoria selecionada
  const selectedCategory = categories.find(c => c.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoading}
          className={cn('w-full justify-between', className)}
        >
          {selectedCategory ? (
            <div className="flex items-center gap-2 truncate">
              <div
                className="h-3 w-3 rounded-full shrink-0"
                style={{ backgroundColor: selectedCategory.color }}
              />
              <span className="truncate">{selectedCategory.name}</span>
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar categoria..." />
          <CommandList>
            <CommandEmpty>Nenhuma categoria encontrada.</CommandEmpty>
            <CommandGroup>
              {/* Opção para limpar seleção */}
              {showRoot && (
                <CommandItem
                  value="__none__"
                  onSelect={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                >
                  <FolderTree className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground italic">Sem categoria</span>
                  {value === null && (
                    <Check className="ml-auto h-4 w-4" />
                  )}
                </CommandItem>
              )}

              {/* Lista de categorias */}
              {flatCategories.map((category) => (
                <CommandItem
                  key={category.id}
                  value={category.name}
                  onSelect={() => {
                    onChange(category.id);
                    setOpen(false);
                  }}
                  className="flex items-center"
                  style={{ paddingLeft: `${8 + category.level * 16}px` }}
                >
                  <div
                    className="mr-2 h-3 w-3 rounded-full shrink-0"
                    style={{ backgroundColor: category.color }}
                  />
                  <span className="truncate">{category.name}</span>
                  {value === category.id && (
                    <Check className="ml-auto h-4 w-4" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
